import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { PencilIcon, ArrowLeftIcon, TrashIcon } from '@heroicons/react/20/solid'
import { createCrudApi } from '../../lib/api'
import { nullToUndefined } from '../../lib/utils'
import { EventForm } from '../../components/forms/EventForm'
import { Avatar } from '../../components/ui/Avatar'
import { useTranslation } from '../../hooks/useTranslation'
import { useUserLookup } from '../../hooks/useUsers'
import { useFls } from '../../hooks/useFls'
import { RecordSharePanel } from '../../components/sharing/RecordSharePanel'
import { AuditHistoryPanel } from '../../components/audit/AuditHistoryPanel'
import type { Event, EventInput } from '../../../shared/types'

const eventsApi = createCrudApi<Event, EventInput>('events')

export function EventDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isEditing, setIsEditing] = useState(false)
  const { t } = useTranslation()
  const { getUser } = useUserLookup()
  const { canRead, canWrite } = useFls('events')

  const { data: event, isLoading } = useQuery({
    queryKey: ['events', id],
    queryFn: () => eventsApi.get(id!),
    enabled: !!id,
  })

  const updateMutation = useMutation({
    mutationFn: (data: Partial<EventInput>) => eventsApi.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events', id] })
      queryClient.invalidateQueries({ queryKey: ['events'] })
      setIsEditing(false)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => eventsApi.delete(id!),
    onSuccess: () => {
      navigate('/events')
    },
  })

  if (isLoading) return <div>{t('common.loading')}</div>
  if (!event) return <div>{t('pages.events.notFound')}</div>

  return (
    <>
      {/* Secondary column (desktop sidebar) */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="mb-6">
          <Link
            to="/events"
            className="inline-flex items-center gap-x-2 text-sm font-semibold hover:opacity-75 text-brand-500"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            {t('pages.events.backToList')}
          </Link>
        </div>

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">{event.subject}</h1>
          <p className="mt-1 text-sm text-gray-500">{event.type}</p>
        </div>

        <div className="space-y-3 mb-6">
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="w-full inline-flex justify-center items-center gap-x-2 rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500"
          >
            <PencilIcon className="h-4 w-4" />
            {isEditing ? t('common.cancel') : t('common.edit')}
          </button>
          <button
            onClick={() => {
              if (confirm(t('pages.events.confirmDelete'))) {
                deleteMutation.mutate()
              }
            }}
            className="w-full inline-flex justify-center items-center gap-x-2 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
          >
            <TrashIcon className="h-4 w-4" />
            {t('common.delete')}
          </button>
        </div>

        <div className="border-t border-gray-200 pt-6">
          <h2 className="text-sm font-semibold text-gray-900 mb-4">{t('common.quickInfo')}</h2>
          <dl className="space-y-4">
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('fields.status')}</dt>
              <dd className="mt-1">
                <span className="inline-flex rounded-full px-2 text-xs font-semibold leading-5 bg-blue-100 text-blue-800">
                  {event.status}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('fields.type')}</dt>
              <dd className="mt-1 text-sm text-gray-900">{event.type}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('fields.start')}</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {new Date(event.startTime).toLocaleString()}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('fields.end')}</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {new Date(event.endTime).toLocaleString()}
              </dd>
            </div>
            {event.relatedToType && (
              <div>
                <dt className="text-xs font-medium text-gray-500">{t('fields.relatedTo')}</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {event.relatedToType} (ID: {event.relatedToId})
                </dd>
              </div>
            )}
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('fields.owner')}</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {(() => {
                  const owner = getUser(event.ownerId)
                  if (!owner) return '-'
                  return (
                    <div className="flex items-center gap-x-2">
                      <Avatar firstName={owner.firstName} lastName={owner.lastName} size="xs" />
                      <span>{owner.firstName} {owner.lastName}</span>
                    </div>
                  )
                })()}
              </dd>
            </div>
          </dl>
        </div>

        {/* Record Sharing */}
        <RecordSharePanel entity="events" entityId={id!} ownerId={event.ownerId ?? undefined} />

        {/* Audit History */}
        <AuditHistoryPanel entity="events" entityId={id!} />
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] p-6">
        {/* Mobile header */}
        <div className="xl:hidden mb-6">
          <div className="mb-4">
            <Link
              to="/events"
              className="inline-flex items-center gap-x-2 text-sm font-semibold hover:opacity-75 text-brand-500"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              {t('pages.events.backToList')}
            </Link>
          </div>
          <div className="flex gap-x-3">
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="inline-flex items-center gap-x-2 rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500"
            >
              <PencilIcon className="h-4 w-4" />
              {isEditing ? t('common.cancel') : t('common.edit')}
            </button>
            <button
              onClick={() => {
                if (confirm(t('pages.events.confirmDelete'))) {
                  deleteMutation.mutate()
                }
              }}
              className="inline-flex items-center gap-x-2 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
            >
              <TrashIcon className="h-4 w-4" />
              {t('common.delete')}
            </button>
          </div>
        </div>

        {/* Detail card */}
        <div className="overflow-hidden bg-white shadow sm:rounded-lg">
          <div className="px-4 py-6 sm:px-6">
            <div>
              <h3 className="text-base font-semibold text-gray-900">{event.subject}</h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">{t('pages.events.detailSubtitle')}</p>
            </div>
          </div>

          {isEditing ? (
            <div className="border-t border-gray-100 px-4 py-6 sm:px-6">
              <EventForm
                defaultValues={nullToUndefined(event) as Partial<EventInput>}
                onSubmit={(data) => updateMutation.mutate(data)}
                onCancel={() => setIsEditing(false)}
                loading={updateMutation.isPending}
                canWriteField={canWrite}
              />
            </div>
          ) : (
            <div className="border-t border-gray-100">
              <dl className="divide-y divide-gray-100">
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">{t('fields.subject')}</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {event.subject}
                  </dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">{t('fields.type')}</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {event.type}
                  </dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">{t('fields.status')}</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    <span className="inline-flex rounded-full px-2 text-xs font-semibold leading-5 bg-blue-100 text-blue-800">
                      {event.status}
                    </span>
                  </dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">{t('fields.start')}</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {new Date(event.startTime).toLocaleString()}
                  </dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">{t('fields.end')}</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {new Date(event.endTime).toLocaleString()}
                  </dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">{t('fields.description')}</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {event.description || '-'}
                  </dd>
                </div>
                {event.relatedToType && (
                  <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">{t('fields.relatedTo')}</dt>
                    <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                      {event.relatedToType} (ID: {event.relatedToId})
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          )}
        </div>
        </div>
      </div>
    </>
  )
}
